/**
 * The catch-up queue (JRNL-03).
 *
 * Days in the last 14 with no notes, so gaps can be cleared without hunting
 * through the calendar.
 *
 * **The queue is derived, never materialised.** It is the set of dates in the
 * window that §6.2 did not return — no row is written for a gap, here or
 * anywhere, until the user saves a note. `data-model/0004` §1: *"It writes a
 * fact nobody observed into a document that gets signed off."*
 *
 * Each row is the weekday and the date — "Tuesday 27 May" — and nothing else,
 * as the design has it. It briefly carried a "· 7 days ago" suffix too; the
 * rows are already most-recent-first inside a panel whose summary names the
 * window, so the suffix restated their position rather than adding to it.
 *
 * **The caller must pass day summaries covering at least the last 14 days.**
 * Absence is the whole signal, so a `days` array fetched for a narrower range
 * would report dates as gaps that are nothing of the kind.
 */
import type { JournalDaySummary } from "@gb/contracts";
import { SectionLabel } from "@/components/gb/section-label";
import { addDays, DISPLAY_LOCALE } from "@/lib/format";
import { resolveWindowStart, todayIso } from "./journal-calendar";

/**
 * How far back the queue looks.
 *
 * Fourteen days ending **yesterday**, not ending today. Today is not a day
 * somebody failed to write up — it is a day still being worked, and it has its
 * own pinned prompt at the top of the feed (§7.6). Listing it here would put
 * two affordances on one date and label it "0 days ago".
 */
const QUEUE_DAYS = 14;

/** `"Tuesday 27 May"` — the weekday matters on a site record. */
function gapLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(DISPLAY_LOCALE, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/**
 * The gap dates, most recent first.
 *
 * Exported for its own test: this function *is* the feature, and it is the one
 * place where "derived from absence" is either true or quietly not.
 */
export function gapsInWindow(
  days: readonly JournalDaySummary[],
  windowStart: string | null,
  today: string,
): string[] {
  const from = resolveWindowStart(windowStart, days);
  // `noteCount === 0` rather than "not in the array": a date returned only
  // because photographs were dropped on it still holds no notes, and the queue
  // is a list of days nobody wrote up.
  const logged = new Set(
    days.filter((day) => day.noteCount > 0).map((day) => day.date),
  );

  const gaps: string[] = [];
  for (let back = 1; back <= QUEUE_DAYS; back++) {
    const date = addDays(today, -back);
    // Never before the site existed. An entry for a day before the window is a
    // gap that was never a gap.
    if (date < from) continue;
    if (!logged.has(date)) gaps.push(date);
  }
  return gaps;
}

export function CatchUpQueue({
  days,
  windowStart,
  onFillIn,
}: {
  days: JournalDaySummary[];
  windowStart: string | null;
  onFillIn: (date: string) => void;
}) {
  const today = todayIso();
  const gaps = gapsInWindow(days, windowStart, today);

  return (
    // `relative` for the same reason the calendar's root carries it: this
    // subtree contains its own out-of-flow children rather than letting them
    // extend the page's scroll height (`architecture/0001`).
    // Dark, per the design: this is the one panel in the rail that asks for
    // something rather than reporting it, and `bg-chrome` is the app's weight
    // for that (the sidebar, the Site Brain hero, the compliance banner head).
    <section className="relative rounded-card-lg bg-chrome p-4 shadow-card">
      <SectionLabel className="mb-1.5 text-brand">Catch-up queue</SectionLabel>
      <p className="text-[0.82rem] leading-[1.5] text-faint">
        {gaps.length === 0
          ? `No gaps in the last ${QUEUE_DAYS} days.`
          : `${gaps.length} ${gaps.length === 1 ? "gap" : "gaps"} in the last ${QUEUE_DAYS} days. Work through them one at a time — each opens pre-filled from site data.`}
      </p>

      {gaps.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {gaps.map((date) => (
            // The date alone, as the design has it. It briefly carried a
            // "· N days ago" suffix; the row is already ordered most-recent
            // first inside a panel whose own summary names the window, so the
            // suffix restated the position rather than adding to it.
            <li
              key={date}
              className="flex items-center justify-between gap-3 rounded-control border-[1.5px] border-white/10 bg-white/5 px-3 py-2"
            >
              <span className="min-w-0 text-[0.86rem] leading-[1.4] font-semibold text-surface">
                {gapLabel(date)}
              </span>
              <button
                type="button"
                className="shrink-0 font-display text-[0.74rem] font-bold tracking-[0.06em] text-brand-glow uppercase transition-colors hover:text-brand-soft"
                // Named for a screen reader, which hears a column of
                // identical "Fill in" buttons otherwise.
                aria-label={`Fill in ${gapLabel(date)}`}
                onClick={() => onFillIn(date)}
              >
                Fill in
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
